/**
 * Read-only preview table for the HR leave-history import dry-run.
 *
 * Renders each HistoryRowResult as a row: skip toggle, status badge, employee
 * number, matched user (name + email, with a "name differs" warning when
 * nameMismatch), leave type, dates, days, leave status, and a notes/error
 * column. Mirrors LeaveImportPreviewTable so the two imports feel identical.
 */

import type { HistoryRowResult } from '../../api/hr-leave-history'

interface Props {
  rows: HistoryRowResult[]
  /** Row indices HR has chosen to exclude from the import. */
  skipped: ReadonlySet<number>
  onToggleSkip: (rowIndex: number) => void
}

const ACTION_LABEL: Record<HistoryRowResult['action'], string> = {
  create: 'New',
  skip: 'Already in',
  error: 'Error',
}

function actionBadge(action: HistoryRowResult['action']): React.CSSProperties {
  switch (action) {
    case 'create':
      return { background: 'var(--success-bg)', color: 'var(--success-fg)' }
    case 'skip':
      return { background: 'var(--surface-2)', color: 'var(--text-3)' }
    case 'error':
      return { background: 'var(--danger-bg)', color: 'var(--danger-fg)' }
  }
}

function whenLabel(r: HistoryRowResult): string {
  if (r.startDate == null) return '—'
  if (r.endDate == null || r.endDate === r.startDate) return r.startDate
  return `${r.startDate} → ${r.endDate}`
}

export function HistoryImportPreviewTable({ rows, skipped, onToggleSkip }: Props): JSX.Element {
  return (
    <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
      <table
        aria-label="Import preview"
        style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%' }}
      >
        <thead>
          <tr style={{ background: 'var(--surface-2)' }}>
            <HeaderCell>Skip</HeaderCell>
            <HeaderCell>Status</HeaderCell>
            <HeaderCell>Employee No.</HeaderCell>
            <HeaderCell>Matched user</HeaderCell>
            <HeaderCell>Leave type</HeaderCell>
            <HeaderCell>When</HeaderCell>
            <HeaderCell>Days</HeaderCell>
            <HeaderCell>Leave status</HeaderCell>
            <HeaderCell>Notes</HeaderCell>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isSkipped = skipped.has(r.rowIndex)
            return (
              <tr
                key={String(r.rowIndex)}
                data-skipped={isSkipped ? 'true' : 'false'}
                style={{
                  background: isSkipped
                    ? 'var(--surface-2)'
                    : r.action === 'error'
                      ? 'var(--danger-bg)'
                      : undefined,
                  opacity: isSkipped ? 0.5 : 1,
                  textDecoration: isSkipped ? 'line-through' : 'none',
                  transition: 'opacity 120ms ease, background 120ms ease',
                }}
              >
                {/* Skip toggle — errors can't be imported, so no toggle there */}
                <td style={cellStyle}>
                  {r.action === 'error' ? (
                    <span style={{ color: 'var(--text-3)' }}>—</span>
                  ) : (
                    <button
                      type="button"
                      data-testid="skip-toggle"
                      aria-pressed={isSkipped}
                      onClick={() => onToggleSkip(r.rowIndex)}
                      style={{
                        padding: '2px 8px',
                        borderRadius: 4,
                        border: '1px solid var(--border)',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                        background: isSkipped ? 'var(--steel-blue)' : 'var(--surface)',
                        color: isSkipped ? 'var(--surface)' : 'var(--text-2)',
                      }}
                    >
                      {isSkipped ? 'Skipped' : 'Skip'}
                    </button>
                  )}
                </td>

                {/* Status badge */}
                <td style={cellStyle}>
                  <span
                    style={{
                      ...actionBadge(r.action),
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {ACTION_LABEL[r.action]}
                  </span>
                </td>

                {/* Employee number */}
                <td style={{ ...cellStyle, fontFamily: 'monospace' }}>{r.employeeNo}</td>

                {/* Matched user */}
                <td style={cellStyle}>
                  {r.matchedEmployeeEmail != null ? (
                    <span>
                      <span style={{ fontWeight: 500 }}>{r.matchedEmployeeName}</span>
                      <br />
                      <span style={{ color: 'var(--text-3)' }}>{r.matchedEmployeeEmail}</span>
                      {r.nameMismatch ? (
                        <span
                          title={`CSV said: ${r.csvEmployeeName}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 3,
                            marginLeft: 6,
                            padding: '1px 6px',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            background: 'var(--warning-bg, var(--danger-bg))',
                            color: 'var(--warning-fg, var(--danger-fg))',
                            cursor: 'help',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          ⚠ name differs
                        </span>
                      ) : null}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--text-3)' }}>
                      {r.csvEmployeeName.trim().length > 0
                        ? `no match for ${r.csvEmployeeName.trim()}`
                        : 'no match'}
                    </span>
                  )}
                </td>

                {/* Leave type */}
                <td style={cellStyle}>{r.leaveType ?? '—'}</td>

                {/* When */}
                <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{whenLabel(r)}</td>

                {/* Days */}
                <td style={{ ...cellStyle, textAlign: 'right' }}>{r.days ?? '—'}</td>

                {/* Leave status */}
                <td style={cellStyle}>
                  {r.leaveStatus != null ? (
                    <span style={{ textTransform: 'capitalize' }}>{r.leaveStatus}</span>
                  ) : (
                    '—'
                  )}
                </td>

                {/* Notes / error */}
                <td
                  style={{
                    ...cellStyle,
                    color: r.action === 'error' ? 'var(--danger-fg)' : 'var(--text-3)',
                    minWidth: 140,
                  }}
                >
                  {r.error ?? r.note ?? ''}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const cellStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'middle',
}

function HeaderCell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <th
      scope="col"
      style={{
        padding: '8px 10px',
        textAlign: 'left',
        fontWeight: 600,
        color: 'var(--text-3)',
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        borderBottom: '1px solid var(--border)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  )
}
